/**
 * Tema claro / escuro da aplicacao.
 *
 * O tema e representado pelo atributo `data-theme` no elemento <html>:
 *
 *     <html>                      -> tema claro (ausencia do atributo)
 *     <html data-theme="dark">    -> tema escuro
 *
 * As cores vivem em Content/tema.css, no bloco `:root[data-theme="dark"]`.
 * No tema claro as variaveis --mu-* ficam indefinidas de proposito e cada
 * declaracao cai no seu literal original (o fallback do `var()`), por isso o
 * tema claro renderiza exatamente como antes desta feature existir.
 *
 * A preferencia fica em localStorage (nao sessionStorage): e uma preferencia
 * do usuario, deve sobreviver a fechar o navegador. Nao ha `localStorage.clear()`
 * global no projeto, so `removeItem` de chaves especificas da cesta, entao a
 * escolha nao e perdida no login.
 *
 * Sem escolha salva (primeiro acesso), vale o tema do sistema operacional
 * (`prefers-color-scheme`). A partir do primeiro clique no botao a escolha do
 * usuario passa a mandar e o sistema operacional deixa de ser consultado.
 *
 * Para evitar "flash" branco antes do bundle executar, o atributo tambem e
 * aplicado por um script inline no <head> de Views/Shared/_Base.cshtml E de
 * Views/Login/Index.cshtml (esta tem Layout = null, nao herda a outra). Se
 * mudar CHAVE_TEMA, o nome do atributo ou a regra de padrao aqui, atualize
 * os dois lugares tambem.
 */

export type Tema = "claro" | "escuro"

/** Mesma chave usada pelo script inline de _Base.cshtml. */
const CHAVE_TEMA = "mu-tema"
const ATRIBUTO = "data-theme"
const VALOR_ESCURO = "dark"

type Ouvinte = (tema: Tema) => void

const ouvintes: Ouvinte[] = []

const lerPreferenciaSalva = (): Tema | null => {
    try {
        const salvo = localStorage.getItem(CHAVE_TEMA)
        return salvo === "escuro" || salvo === "claro" ? salvo : null
    } catch {
        // localStorage pode lancar em modo restrito/privado — trata como "sem preferencia"
        return null
    }
}

/** Consulta o tema configurado no sistema operacional. */
const consultaSistemaEscuro = (): MediaQueryList | null => {
    try {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return null
        }
        return window.matchMedia("(prefers-color-scheme: dark)")
    } catch {
        return null
    }
}

const sistemaPrefereEscuro = (): boolean => consultaSistemaEscuro()?.matches === true

/**
 * Tema que deve valer agora: a escolha explicita do usuario, se houver; senao o
 * tema do sistema operacional (primeiro acesso).
 */
export const temaEfetivo = (): Tema =>
    lerPreferenciaSalva() ?? (sistemaPrefereEscuro() ? "escuro" : "claro")

/**
 * `false` enquanto o usuario nunca escolheu um tema — o que esta em vigor e o
 * tema do sistema operacional (ver temaEfetivo). Passa a `true` no primeiro
 * clique no botao (definirTema grava em localStorage) e nunca volta.
 *
 * Usado para destacar o botao de tema para quem ainda nao sabe que ele existe
 * (ver BotaoAlternarTema/`destacarQuandoSemPreferencia`).
 */
export const possuiPreferenciaTemaSalva = (): boolean => lerPreferenciaSalva() !== null

/**
 * Tema em vigor agora, lido do DOM (fonte da verdade em tempo de execucao).
 * Le do DOM e nao do localStorage porque o script inline do <head> ja aplicou
 * o atributo antes deste modulo existir.
 */
export const obterTema = (): Tema =>
    document.documentElement.getAttribute(ATRIBUTO) === VALOR_ESCURO ? "escuro" : "claro"

export const estaEscuro = (): boolean => obterTema() === "escuro"

/**
 * Aplica o tema no DOM, persiste a escolha e avisa os inscritos.
 * Chamar com o tema que ja esta em vigor e no-op (nao notifica).
 */
export const definirTema = (tema: Tema): void => {
    if (obterTema() === tema) {
        return
    }

    if (tema === "escuro") {
        document.documentElement.setAttribute(ATRIBUTO, VALOR_ESCURO)
    } else {
        document.documentElement.removeAttribute(ATRIBUTO)
    }

    try {
        localStorage.setItem(CHAVE_TEMA, tema)
    } catch {
        // sem persistencia (modo privado): o tema vale so para esta pagina
    }

    ouvintes.forEach(ouvinte => ouvinte(tema))
}

export const alternarTema = (): Tema => {
    const proximo: Tema = estaEscuro() ? "claro" : "escuro"
    definirTema(proximo)
    return proximo
}

/**
 * Inscreve um callback para mudancas de tema. Retorna a funcao de cancelamento
 * (pronta para ser devolvida direto de um useEffect).
 */
export const assinarTema = (ouvinte: Ouvinte): (() => void) => {
    ouvintes.push(ouvinte)
    return () => {
        const indice = ouvintes.indexOf(ouvinte)
        if (indice >= 0) {
            ouvintes.splice(indice, 1)
        }
    }
}

/**
 * Reaplica o atributo a partir do que esta salvo. O script inline do <head> ja
 * faz isso no carregamento; serve para telas montadas dinamicamente e para
 * sincronizar quando outra aba troca o tema.
 */
export const sincronizarTema = (): void => {
    aplicarNoDom(temaEfetivo())
}

const aplicarNoDom = (tema: Tema): void => {
    if (tema === "escuro") {
        document.documentElement.setAttribute(ATRIBUTO, VALOR_ESCURO)
    } else {
        document.documentElement.removeAttribute(ATRIBUTO)
    }
}

// Enquanto o usuario NAO fez uma escolha explicita, acompanha o tema do sistema
// operacional em tempo real. Depois de clicar no botao, a escolha dele manda e
// este listener nao mexe mais em nada (lerPreferenciaSalva passa a devolver valor).
const consultaSistema = consultaSistemaEscuro()
if (consultaSistema) {
    const aoMudarSistema = (evento: MediaQueryListEvent | MediaQueryList) => {
        if (lerPreferenciaSalva() !== null) {
            return
        }
        const tema: Tema = evento.matches ? "escuro" : "claro"
        if (obterTema() !== tema) {
            aplicarNoDom(tema)
            ouvintes.forEach(ouvinte => ouvinte(tema))
        }
    }
    if (typeof consultaSistema.addEventListener === "function") {
        consultaSistema.addEventListener("change", aoMudarSistema)
    } else if (typeof consultaSistema.addListener === "function") {
        // Safari antigo / WebView: API depreciada, mas e a unica disponivel
        consultaSistema.addListener(aoMudarSistema)
    }
}

// Troca de tema feita em outra aba: o evento `storage` so dispara nas OUTRAS
// abas, entao nao ha risco de laco com definirTema().
if (typeof window !== "undefined") {
    window.addEventListener("storage", evento => {
        if (evento.key !== CHAVE_TEMA) {
            return
        }
        const tema: Tema = evento.newValue === "escuro" ? "escuro" : "claro"
        if (obterTema() !== tema) {
            if (tema === "escuro") {
                document.documentElement.setAttribute(ATRIBUTO, VALOR_ESCURO)
            } else {
                document.documentElement.removeAttribute(ATRIBUTO)
            }
            ouvintes.forEach(ouvinte => ouvinte(tema))
        }
    })
}

/**
 * Exposto em `window.temaMU` por main-app.js — as views .cshtml sao servidas
 * como JS puro (sem bundler), entao nao podem importar este modulo.
 */
export const temaMU = {
    obter: obterTema,
    estaEscuro,
    definir: definirTema,
    alternar: alternarTema,
    assinar: assinarTema,
    sincronizar: sincronizarTema,
    efetivo: temaEfetivo,
    possuiPreferencia: possuiPreferenciaTemaSalva,
}
